import type { DocumentSummary, Reservation } from '@horevia/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  EmptyState,
  ErrorState,
  NoPermissionState,
  Notice,
  PageHeader,
  Progress,
  Skeleton,
  StatusBadge,
  Tab,
  TabPanel,
  Tabs,
  TabsList,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
  toast,
} from '@horevia/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Ban,
  BedDouble,
  Building2,
  Check,
  Clock,
  CreditCard,
  FileText,
  History,
  Mail,
  Pencil,
  ShieldCheck,
  UserX,
  LogIn,
  LogOut,
  Plus,
  Utensils,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan, useSession } from '@/features/auth/session';
import { ComplianceChecklist } from '@/features/compliance/ComplianceChecklist';
import { complianceGauge, reservationComplianceQueryOptions } from '@/features/compliance/queries';
import { DocumentDeleteDialog, DocumentList } from '@/features/documents/DocumentList';
import { DocumentPreviewDialog } from '@/features/documents/DocumentPreviewDialog';
import { DocumentUploader } from '@/features/documents/DocumentUploader';
import { deleteDocument, documentKeys, documentsQueryOptions } from '@/features/documents/queries';
import { ReservationBillingTab } from '@/features/billing/ReservationBillingTab';
import { FolioTab } from '@/features/folio/FolioTab';
import { PaymentDrawer } from '@/features/payments/PaymentDrawer';
import { paymentsQueryOptions } from '@/features/payments/queries';
import { methodTone, paymentBadge } from '@/features/payments/status';
import { EditReservationDrawer } from '@/features/reservations/EditReservationDrawer';
import {
  CancelDialog,
  CheckOutDialog,
  HoldDialog,
  NoShowDialog,
} from '@/features/reservations/ReservationActionDialogs';
import { ReservationErrorNotice } from '@/features/reservations/ReservationErrorNotice';
import {
  cancelReservation,
  checkOutReservation,
  confirmReservation,
  holdReservation,
  noShowReservation,
  confirmationDocumentUrl,
  reservationHistoryQueryOptions,
  reservationKeys,
  reservationQueryOptions,
  sendConfirmation,
} from '@/features/reservations/queries';
import { availableActions, complianceTone, reservationTone } from '@/features/reservations/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDate, formatDateTime, formatTime } from '@/lib/format';

export const Route = createFileRoute('/_app/reservations/$reservationId')({
  component: ReservationPage,
});

type ActiveDialog =
  'none' | 'hold' | 'confirm' | 'checkOut' | 'cancel' | 'noShow' | 'deposit' | 'send';

function ReservationPage() {
  const { reservationId } = Route.useParams();
  const { t, i18n } = useTranslation([
    'reservations',
    'payments',
    'documents',
    'compliance',
    'customers',
    'resources',
    'common',
    'errors',
  ]);
  const queryClient = useQueryClient();
  const session = useSession();
  const canRead = useCan('reservation.read');
  const canCheckoutWithBalance = useCan('reservation.checkout.with_balance');
  const canConfirmWithoutDeposit = useCan('reservation.confirm.without_deposit');

  const canReadCompliance = useCan('compliance.read');
  const canReadDocuments = useCan('document.read');
  const canReadFolio = useCan('folio.read');
  const canUploadDocument = useCan('document.upload');
  const canDeleteDocument = useCan('document.delete');

  const [dialog, setDialog] = useState<ActiveDialog>('none');
  const [editOpen, setEditOpen] = useState(false);
  const [withoutDepositReason, setWithoutDepositReason] = useState('');
  const [previewDocument, setPreviewDocument] = useState<DocumentSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DocumentSummary | null>(null);

  const query = useQuery({ ...reservationQueryOptions(reservationId), enabled: canRead });
  const complianceQuery = useQuery(
    reservationComplianceQueryOptions(reservationId, canRead && canReadCompliance),
  );
  const documentsQuery = useQuery(
    documentsQueryOptions({ reservationId }, canRead && canReadDocuments),
  );

  const deleteMutation = useMutation({
    mutationFn: (input: { id: string; reason: string }) => deleteDocument(input.id, input.reason),
    onSuccess: async () => {
      setDeleteTarget(null);
      await queryClient.invalidateQueries({ queryKey: documentKeys.all });
      await queryClient.invalidateQueries({ queryKey: reservationKeys.all });
      toast.success(t('documents:toasts.deleted'));
    },
  });
  const historyQuery = useQuery(reservationHistoryQueryOptions(reservationId, canRead));
  const depositsQuery = useInfiniteQuery(
    paymentsQueryOptions({ reservationId }, 25, session.can('payment.read')),
  );

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: reservationKeys.all });
  };

  const action = useMutation({
    mutationFn: async (input: { kind: ActiveDialog; payload?: unknown }) => {
      const reservation = query.data;
      if (!reservation) throw new Error('missing');
      switch (input.kind) {
        case 'hold':
          return holdReservation(reservation.id, input.payload as string, reservation.version);
        case 'confirm':
          return confirmReservation(
            reservation.id,
            reservation.version,
            input.payload as string | undefined,
          );
        case 'checkOut':
          return checkOutReservation(reservation.id, {
            version: reservation.version,
            ...(input.payload as object),
          });
        case 'cancel':
          return cancelReservation(reservation.id, {
            version: reservation.version,
            ...(input.payload as { reason: string; feeAmount: number }),
          });
        case 'noShow':
          return noShowReservation(reservation.id, {
            version: reservation.version,
            ...(input.payload as { feeAmount: number }),
          });
        case 'send':
          return sendConfirmation(reservation.id, input.payload as { channels: ['EMAIL'] });
        default:
          throw new Error('unsupported');
      }
    },
    onSuccess: async (_result, variables) => {
      await invalidate();
      setDialog('none');
      setWithoutDepositReason('');
      toast.success(t(`reservations:toasts.${variables.kind}`));
    },
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={100} />
          <Skeleton height={360} />
        </div>
      </Container>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const reservation: Reservation = query.data;
  const actions = availableActions(reservation, session.can);
  const deposits = depositsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const history = historyQuery.data ?? [];
  const compliance = complianceQuery.data;
  const gauge = complianceGauge(compliance?.items ?? []);
  const complianceStatus = compliance?.status ?? reservation.compliance.status;
  /** Pièces hors exigences de conformité : contrat, bon de commande, photo… */
  const otherDocuments = (documentsQuery.data ?? []).filter(
    (document) => document.type !== 'IDENTITY_DOCUMENT' && document.type !== 'POLICE_FORM',
  );

  return (
    <Container>
      <PageHeader
        title={reservation.reference}
        subtitle={t('reservations:detail.subtitle', {
          guest: reservation.guest.displayName,
          resource: reservation.resource.name,
          from: formatDate(reservation.arrivalAt, i18n.language),
          to: formatDate(reservation.departureAt, i18n.language),
          nights: reservation.nights ?? 1,
          billedTo: reservation.billingParty.displayName,
        })}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('reservations:title'),
            link: <Link to="/reservations">{t('reservations:title')}</Link>,
          },
          { label: reservation.reference },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            <StatusBadge status={reservationTone(reservation.status)} large>
              {t(`reservations:status.${reservation.status}`)}
            </StatusBadge>
            <Badge tone={complianceTone(reservation.compliance.status)} size="lg">
              <ShieldCheck className="ico ico-14" aria-hidden="true" />
              {t(`reservations:compliance.${reservation.compliance.status}`)}
            </Badge>
          </div>
        }
      />

      <div className="money-strip mb-5">
        <div className="money-strip__cell">
          <div className="money-strip__l">{t('reservations:summary.net')}</div>
          <div className="money-strip__v">
            {formatAmount(reservation.totals.net, i18n.language)}
          </div>
          <div className="t-12 muted mt-1">
            {t('reservations:detail.netDetail', {
              subtotal: formatAmount(reservation.totals.subtotal, i18n.language),
            })}
            {reservation.totals.folioTotal > 0
              ? ` · ${t('reservations:detail.folioDetail', {
                  amount: formatAmount(reservation.totals.folioTotal, i18n.language),
                })}`
              : ''}
          </div>
        </div>
        <div className="money-strip__cell money-strip__cell--paid">
          <div className="money-strip__l">{t('reservations:summary.paid')}</div>
          <div className="money-strip__v">
            {formatAmount(reservation.totals.paid, i18n.language)}
          </div>
          <div className="t-12 muted mt-1">
            {t('reservations:detail.depositsCount', { count: deposits.length })}
          </div>
        </div>
        <div className="money-strip__cell money-strip__cell--due">
          <div className="money-strip__l">{t('reservations:summary.balance')}</div>
          <div className="money-strip__v">
            {formatAmount(reservation.totals.balance, i18n.language)}
          </div>
          {reservation.totals.balance > 0 ? (
            <div className="t-12 mt-1" style={{ color: 'var(--warning)' }}>
              {t('reservations:detail.balanceDue')}
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid--main-aside">
        <div>
          <Tabs defaultValue="stay">
            <TabsList pills aria-label={t('reservations:detail.tabsLabel')}>
              <Tab value="stay" icon={<BedDouble className="ico" aria-hidden="true" />}>
                {t('reservations:detail.tabs.stay')}
              </Tab>
              <Tab value="customer" icon={<Building2 className="ico" aria-hidden="true" />}>
                {t('reservations:detail.tabs.customer')}
              </Tab>
              <Tab
                value="documents"
                icon={<ShieldCheck className="ico" aria-hidden="true" />}
                count={
                  gauge.total - gauge.validated > 0 ? gauge.total - gauge.validated : undefined
                }
              >
                {t('reservations:detail.tabs.documents')}
              </Tab>
              <Tab
                value="payments"
                icon={<CreditCard className="ico" aria-hidden="true" />}
                count={deposits.length}
              >
                {t('reservations:detail.tabs.payments')}
              </Tab>
              {canReadFolio ? (
                <Tab
                  value="folio"
                  icon={<Utensils className="ico" aria-hidden="true" />}
                  count={reservation.folio?.chargesCount || undefined}
                >
                  {t('reservations:detail.tabs.folio')}
                </Tab>
              ) : null}
              <Tab value="history" icon={<History className="ico" aria-hidden="true" />}>
                {t('reservations:detail.tabs.history')}
              </Tab>
            </TabsList>

            {/* ------------------------- Séjour ------------------------- */}
            <TabPanel value="stay">
              <div className="stack">
                <Card>
                  <CardHeader
                    title={t('reservations:detail.stayTitle')}
                    icon={<BedDouble className="ico ico-16" aria-hidden="true" />}
                    actions={
                      actions.edit ? (
                        <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
                          <Pencil className="ico ico-14" aria-hidden="true" />
                          {t('common:actions.edit')}
                        </Button>
                      ) : null
                    }
                  />
                  <CardBody>
                    <div className="kv-grid">
                      <div>
                        <div className="kv__k">{t('reservations:fields.resource')}</div>
                        <div className="kv__v">
                          <Link
                            to="/resources/$resourceId"
                            params={{ resourceId: reservation.resource.id }}
                          >
                            {reservation.resource.name}
                          </Link>
                        </div>
                      </div>
                      <div>
                        <div className="kv__k">{t('reservations:fields.property')}</div>
                        <div className="kv__v">{session.user.properties[0]?.name}</div>
                      </div>
                      <div>
                        <div className="kv__k">{t('reservations:fields.arrival')}</div>
                        <div className="kv__v">
                          {formatDate(reservation.arrivalAt, i18n.language)} ·{' '}
                          {formatTime(reservation.arrivalAt, i18n.language)}
                          {reservation.earlyCheckinRequested ? (
                            <span className="t-12 muted" style={{ display: 'block' }}>
                              {t('reservations:detail.earlyCheckin')}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div>
                        <div className="kv__k">{t('reservations:fields.departure')}</div>
                        <div className="kv__v">
                          {formatDate(reservation.departureAt, i18n.language)} ·{' '}
                          {formatTime(reservation.departureAt, i18n.language)}
                        </div>
                      </div>
                      <div>
                        <div className="kv__k">{t('reservations:fields.nights')}</div>
                        <div className="kv__v">{reservation.nights ?? '—'}</div>
                      </div>
                      <div>
                        <div className="kv__k">{t('reservations:fields.occupants')}</div>
                        <div className="kv__v">
                          {t('reservations:adultsCount', { count: reservation.adults })}
                          {reservation.children > 0
                            ? ` · ${t('reservations:childrenCount', { count: reservation.children })}`
                            : ''}
                        </div>
                      </div>
                      <div>
                        <div className="kv__k">{t('reservations:fields.source')}</div>
                        <div className="kv__v">
                          {t(`reservations:source.${reservation.source}`)}
                        </div>
                      </div>
                      <div>
                        <div className="kv__k">{t('reservations:fields.createdAt')}</div>
                        <div className="kv__v">
                          {formatDate(reservation.createdAt, i18n.language)}
                          {reservation.agentName ? ` · ${reservation.agentName}` : ''}
                        </div>
                      </div>
                    </div>

                    <hr />

                    <div className="panel-title">{t('reservations:detail.linesTitle')}</div>
                    <TableWrap>
                      <Table compact>
                        <thead>
                          <tr>
                            <Th>{t('reservations:detail.lineLabel')}</Th>
                            <Th align="right">{t('reservations:detail.lineUnitPrice')}</Th>
                            <Th align="right">{t('reservations:detail.lineQuantity')}</Th>
                            <Th align="right">{t('reservations:detail.lineAmount')}</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {reservation.lines.map((line) => (
                            <Tr key={line.id}>
                              <Td strong>{line.label}</Td>
                              <Td align="right" numeric>
                                {formatAmount(line.unitPrice, i18n.language)}
                              </Td>
                              <Td align="right" numeric>
                                {line.quantity}
                              </Td>
                              <Td align="right" numeric>
                                {formatAmount(line.amount, i18n.language)}
                              </Td>
                            </Tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={3} className="right muted">
                              {t('reservations:summary.subtotal')}
                            </td>
                            <td className="right amount">
                              {formatAmount(reservation.totals.subtotal, i18n.language)}
                            </td>
                          </tr>
                          {reservation.totals.discountTotal > 0 ? (
                            <tr>
                              <td colSpan={3} className="right muted">
                                {t('reservations:summary.discount')}
                              </td>
                              <td className="right amount" style={{ color: 'var(--success)' }}>
                                −{formatAmount(reservation.totals.discountTotal, i18n.language)}
                              </td>
                            </tr>
                          ) : null}
                          {reservation.totals.withholdingTotal > 0 ? (
                            <tr>
                              <td colSpan={3} className="right muted">
                                {t('reservations:summary.withholding')}
                              </td>
                              <td className="right amount" style={{ color: 'var(--success)' }}>
                                −{formatAmount(reservation.totals.withholdingTotal, i18n.language)}
                              </td>
                            </tr>
                          ) : null}
                          <tr>
                            <td colSpan={3} className="right">
                              <b>{t('reservations:summary.net')}</b>
                            </td>
                            <td className="right">
                              <b className="t-16">
                                {formatAmount(reservation.totals.net, i18n.language)}
                              </b>
                            </td>
                          </tr>
                        </tfoot>
                      </Table>
                    </TableWrap>
                  </CardBody>
                </Card>

                {reservation.notesInternal ? (
                  <Card>
                    <CardHeader
                      title={t('reservations:detail.notesTitle')}
                      icon={<FileText className="ico ico-16" aria-hidden="true" />}
                      iconTone="blue"
                    />
                    <CardBody>
                      <p className="t-13 mb-0">{reservation.notesInternal}</p>
                    </CardBody>
                  </Card>
                ) : null}
              </div>
            </TabPanel>

            {/* ------------------- Client & facturation ------------------ */}
            <TabPanel value="customer">
              <div className="stack">
                <Card>
                  <CardHeader
                    title={t('reservations:detail.guestTitle')}
                    icon={<BedDouble className="ico ico-16" aria-hidden="true" />}
                    iconTone="green"
                    actions={
                      <Link
                        to="/customers/$customerId"
                        params={{ customerId: reservation.guest.id }}
                        className="link-quiet t-13"
                      >
                        {t('reservations:detail.openCustomer')}
                      </Link>
                    }
                  />
                  <CardBody>
                    <div className="row row--top" style={{ gap: 16 }}>
                      <Avatar name={reservation.guest.displayName} size="xl" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="t-18 strong">{reservation.guest.displayName}</div>
                        <div className="row row--wrap mt-2" style={{ gap: 8 }}>
                          {reservation.guest.phonePrimary ? (
                            <Badge tone="neutral">{reservation.guest.phonePrimary}</Badge>
                          ) : null}
                          {reservation.guest.email ? (
                            <Badge tone="neutral">{reservation.guest.email}</Badge>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader
                    title={t('reservations:detail.billingTitle')}
                    icon={<Building2 className="ico ico-16" aria-hidden="true" />}
                    actions={
                      <Badge tone={reservation.billingParty.kind === 'COMPANY' ? 'blue' : 'violet'}>
                        {t(`customers:kind.${reservation.billingParty.kind}`)}
                      </Badge>
                    }
                  />
                  <CardBody>
                    <div className="row row--top" style={{ gap: 16 }}>
                      <Avatar name={reservation.billingParty.displayName} size="xl" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="t-18 strong">{reservation.billingParty.displayName}</div>
                        <Link
                          to="/customers/$customerId"
                          params={{ customerId: reservation.billingParty.id }}
                          className="link-quiet t-13"
                        >
                          {t('reservations:detail.openBillingParty')}
                        </Link>
                      </div>
                    </div>
                    {reservation.totals.withholdingTotal > 0 ? (
                      <Notice tone="violet" className="mt-4">
                        <p className="mb-0 t-13">{t('reservations:detail.withholdingApplied')}</p>
                      </Notice>
                    ) : null}
                  </CardBody>
                </Card>

                {/* Synthèse de facturation du séjour (sprint 2.5, SCR-032). */}
                <ReservationBillingTab
                  reservationId={reservation.id}
                  reservationReference={reservation.reference}
                  billingParty={reservation.billingParty}
                  guestName={reservation.guest.displayName}
                  subject={t('reservations:detail.billingSubject', {
                    resource: reservation.resource.name,
                    from: formatDate(reservation.arrivalAt, i18n.language),
                    to: formatDate(reservation.departureAt, i18n.language),
                  })}
                  applyWithholding={reservation.applyWithholding}
                />
              </div>
            </TabPanel>

            {/* ------------------ Documents & conformité ----------------- */}
            <TabPanel value="documents">
              <div className="stack">
                {complianceStatus === 'INCOMPLETE' ? (
                  <Notice
                    tone="warning"
                    title={t('reservations:detail.complianceIncompleteTitle', {
                      count: gauge.total - gauge.validated,
                    })}
                    actions={
                      actions.checkIn ? (
                        <Button variant="primary" size="sm" asChild>
                          <Link
                            to="/reservations/$reservationId/check-in"
                            params={{ reservationId: reservation.id }}
                          >
                            {t('reservations:actions.checkIn')}
                          </Link>
                        </Button>
                      ) : null
                    }
                  >
                    <p className="mb-2">{t('reservations:detail.complianceIncompleteBody')}</p>
                    {(compliance?.missing ?? reservation.compliance.missing).length > 0 ? (
                      <p className="mb-0 t-13">
                        {t('reservations:detail.complianceMissingTypes', {
                          types: (compliance?.missing ?? reservation.compliance.missing)
                            .map((type) => t(`documents:type.${type}`))
                            .join(' · '),
                        })}
                      </p>
                    ) : null}
                  </Notice>
                ) : complianceStatus === 'COMPLETE' ? (
                  <Notice tone="success" title={t('reservations:detail.complianceCompleteTitle')}>
                    <p className="mb-0">{t('reservations:detail.complianceCompleteBody')}</p>
                  </Notice>
                ) : (
                  <Notice tone="info">
                    <p className="mb-0">{t('reservations:detail.complianceNotRequired')}</p>
                  </Notice>
                )}

                <Card>
                  <CardHeader
                    title={t('reservations:detail.requirementsTitle')}
                    icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
                    iconTone="red"
                    actions={
                      <span className="t-13 muted">
                        {t('reservations:detail.requirementsCount', {
                          validated: gauge.validated,
                          total: gauge.total,
                        })}
                      </span>
                    }
                  />
                  <CardBody>
                    {complianceQuery.isPending ? (
                      <Skeleton height={140} />
                    ) : compliance ? (
                      <ComplianceChecklist
                        reservationId={reservation.id}
                        compliance={compliance}
                        onPreview={setPreviewDocument}
                      />
                    ) : (
                      <Notice tone="info">
                        <p className="mb-0 t-13">
                          {t('reservations:detail.complianceNotRequired')}
                        </p>
                      </Notice>
                    )}
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader
                    title={t('documents:others.title')}
                    icon={<FileText className="ico ico-16" aria-hidden="true" />}
                    iconTone="blue"
                    actions={<span className="t-13 muted">{t('documents:others.subtitle')}</span>}
                  />
                  <CardBody>
                    <div className="stack">
                      <DocumentList
                        documents={otherDocuments}
                        emptyTitle={t('documents:others.emptyTitle')}
                        emptyBody={t('documents:others.emptyBody')}
                        onPreview={setPreviewDocument}
                        onDelete={canDeleteDocument ? setDeleteTarget : undefined}
                      />
                      {canUploadDocument ? (
                        <DocumentUploader
                          reservationId={reservation.id}
                          onUploaded={async () => {
                            await queryClient.invalidateQueries({ queryKey: documentKeys.all });
                            toast.success(t('documents:toasts.uploaded'));
                          }}
                        />
                      ) : null}
                    </div>
                  </CardBody>
                </Card>
              </div>
            </TabPanel>

            {/* ------------------------ Paiements ----------------------- */}
            <TabPanel value="payments">
              <Card>
                <CardHeader
                  title={t('reservations:detail.paymentsTitle')}
                  icon={<CreditCard className="ico ico-16" aria-hidden="true" />}
                  iconTone="green"
                  actions={
                    actions.recordDeposit ? (
                      <Button variant="primary" size="sm" onClick={() => setDialog('deposit')}>
                        <Plus className="ico ico-14" aria-hidden="true" />
                        {t('reservations:actions.recordDeposit')}
                      </Button>
                    ) : null
                  }
                />
                <CardBody density="flush">
                  {deposits.length === 0 ? (
                    <EmptyState
                      icon={<CreditCard className="ico ico-24" aria-hidden="true" />}
                      title={t('reservations:detail.paymentsEmptyTitle')}
                      description={t('reservations:detail.paymentsEmptyBody')}
                    />
                  ) : (
                    <TableWrap>
                      <Table>
                        <thead>
                          <tr>
                            <Th>{t('reservations:detail.paymentReference')}</Th>
                            <Th>{t('reservations:detail.paymentDate')}</Th>
                            <Th>{t('reservations:detail.paymentMethod')}</Th>
                            <Th align="right">{t('reservations:detail.paymentAmount')}</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {deposits.map((deposit) => {
                            const badge = paymentBadge(deposit);
                            return (
                              <Tr key={deposit.id}>
                                <Td>
                                  <Link
                                    to="/billing/payments/$paymentId"
                                    params={{ paymentId: deposit.id }}
                                  >
                                    <span className="cell-id">{deposit.reference}</span>
                                  </Link>
                                  <div className="t-12 muted">
                                    <Badge tone={badge.tone} dot>
                                      {t(`payments:${badge.key}`)}
                                    </Badge>
                                  </div>
                                </Td>
                                <Td>
                                  <span className="t-13">
                                    {formatDate(deposit.paidAt, i18n.language)}
                                  </span>
                                </Td>
                                <Td>
                                  <Badge tone={methodTone(deposit.method, deposit.provider)}>
                                    {deposit.provider
                                      ? t(`payments:provider.${deposit.provider}`)
                                      : t(`payments:method.${deposit.method}`)}
                                  </Badge>
                                  {deposit.transactionReference ? (
                                    <div className="t-12 muted mt-1">
                                      {deposit.transactionReference}
                                    </div>
                                  ) : null}
                                </Td>
                                <Td align="right" numeric>
                                  {formatAmount(deposit.amount, i18n.language)}
                                </Td>
                              </Tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </TableWrap>
                  )}
                </CardBody>
                <CardFooter>
                  <span className="t-13 muted">{t('reservations:detail.paymentsFooter')}</span>
                </CardFooter>
              </Card>
            </TabPanel>

            {/* ------------------------ Folio de séjour ----------------- */}
            {canReadFolio ? (
              <TabPanel value="folio">
                <FolioTab reservation={reservation} />
              </TabPanel>
            ) : null}

            {/* ------------------------ Historique ---------------------- */}
            <TabPanel value="history">
              <Card>
                <CardHeader
                  title={t('reservations:detail.historyTitle')}
                  icon={<History className="ico ico-16" aria-hidden="true" />}
                  actions={<Badge tone="neutral">{t('reservations:detail.appendOnly')}</Badge>}
                />
                <CardBody>
                  {history.length === 0 ? (
                    <EmptyState
                      icon={<History className="ico ico-24" aria-hidden="true" />}
                      title={t('reservations:detail.historyEmptyTitle')}
                    />
                  ) : (
                    <div className="timeline">
                      {history.map((entry) => (
                        <div className="tl-item" key={entry.id}>
                          <span className="tl-item__dot">
                            <History className="ico" aria-hidden="true" />
                          </span>
                          <div className="tl-item__title">
                            {t(`reservations:historyActions.${entry.action.replace(/\./g, '_')}`, {
                              defaultValue: entry.action,
                            })}
                            {/* Les entrées hors transition (avance, envoi de
                                confirmation) portent le même statut avant et
                                après : inutile de le répéter. */}
                            {entry.toStatus && entry.toStatus !== entry.fromStatus
                              ? ` — ${t(`reservations:status.${entry.toStatus}`)}`
                              : ''}
                          </div>
                          <div className="tl-item__meta">
                            {formatDateTime(entry.occurredAt, i18n.language)}
                            {entry.actorName ? ` · ${entry.actorName}` : ''}
                          </div>
                          {entry.reason ? (
                            <div className="tl-item__body">{entry.reason}</div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </TabPanel>
          </Tabs>
        </div>

        {/* ---------------------- Panneau d'actions ---------------------- */}
        <aside className="stack sticky-aside">
          <Card variant="raised">
            <CardHeader title={t('reservations:detail.actionsTitle')} />
            <CardBody density="tight">
              {action.isError ? (
                <div className="mb-3">
                  <ReservationErrorNotice error={action.error} />
                </div>
              ) : null}

              <div className="stack stack--sm">
                {actions.checkIn ? (
                  <Button variant="primary" block asChild>
                    <Link
                      to="/reservations/$reservationId/check-in"
                      params={{ reservationId: reservation.id }}
                    >
                      <LogIn className="ico ico-16" aria-hidden="true" />
                      {t('reservations:actions.checkIn')}
                    </Link>
                  </Button>
                ) : null}
                {actions.checkOut ? (
                  <Button variant="primary" block onClick={() => setDialog('checkOut')}>
                    <LogOut className="ico ico-16" aria-hidden="true" />
                    {t('reservations:actions.checkOut')}
                  </Button>
                ) : null}
                {actions.confirm ? (
                  <Button variant="primary" block onClick={() => setDialog('confirm')}>
                    <Check className="ico ico-16" aria-hidden="true" />
                    {t('reservations:actions.confirm')}
                  </Button>
                ) : null}
                {actions.hold ? (
                  <Button variant="secondary" block onClick={() => setDialog('hold')}>
                    <Clock className="ico ico-16" aria-hidden="true" />
                    {t('reservations:actions.hold')}
                  </Button>
                ) : null}
                {actions.recordDeposit ? (
                  <Button variant="secondary" block onClick={() => setDialog('deposit')}>
                    <CreditCard className="ico ico-16" aria-hidden="true" />
                    {t('reservations:actions.recordDeposit')}
                  </Button>
                ) : null}
                {actions.sendConfirmation ? (
                  <Button variant="secondary" block onClick={() => setDialog('send')}>
                    <Mail className="ico ico-16" aria-hidden="true" />
                    {t('reservations:actions.sendConfirmation')}
                  </Button>
                ) : null}
                {actions.edit ? (
                  <Button variant="ghost" block onClick={() => setEditOpen(true)}>
                    <Pencil className="ico ico-16" aria-hidden="true" />
                    {t('common:actions.edit')}
                  </Button>
                ) : null}
              </div>

              {actions.cancel || actions.noShow ? (
                <>
                  <hr />
                  <div className="stack stack--sm">
                    {actions.noShow ? (
                      <Button variant="ghost" block onClick={() => setDialog('noShow')}>
                        <UserX className="ico ico-16" aria-hidden="true" />
                        {t('reservations:actions.noShow')}
                      </Button>
                    ) : null}
                    {actions.cancel ? (
                      <Button variant="danger" block onClick={() => setDialog('cancel')}>
                        <Ban className="ico ico-16" aria-hidden="true" />
                        {t('reservations:actions.cancel')}
                      </Button>
                    ) : null}
                  </div>
                </>
              ) : null}
            </CardBody>
          </Card>

          {reservation.optionExpiresAt ? (
            <Notice tone="warning" title={t('reservations:detail.optionTitle')}>
              <p className="mb-0 t-12">
                {t('reservations:detail.optionBody', {
                  date: formatDateTime(reservation.optionExpiresAt, i18n.language),
                })}
              </p>
            </Notice>
          ) : null}

          <Card>
            <CardHeader
              title={t('reservations:detail.complianceCardTitle')}
              icon={<ShieldCheck className="ico ico-16" aria-hidden="true" />}
              iconTone="amber"
            />
            <CardBody density="tight">
              <div className="row row--between mb-2">
                <span className="t-13 muted">{t('reservations:detail.requiredDocuments')}</span>
                <span className="strong t-13">
                  {gauge.validated} / {gauge.total}
                </span>
              </div>
              <Progress
                value={gauge.validated}
                max={gauge.total || 1}
                tone={gauge.complete ? 'green' : 'amber'}
                label={t('compliance:gaugeLabel')}
                valueText={t('compliance:gauge', {
                  validated: gauge.validated,
                  total: gauge.total,
                })}
              />
              {gauge.awaitingValidation > 0 ? (
                <p className="t-12 muted mb-0 mt-2">
                  {t('compliance:gaugeAwaiting', { count: gauge.awaitingValidation })}
                </p>
              ) : null}
              {!gauge.complete ? (
                <Notice tone="danger" className="mt-4">
                  <p className="mb-0 t-12">{t('reservations:detail.invoiceBlocked')}</p>
                </Notice>
              ) : null}
            </CardBody>
          </Card>
        </aside>
      </div>

      {/* ------------------------- Modales ------------------------------- */}
      <HoldDialog
        open={dialog === 'hold'}
        onOpenChange={(open) => setDialog(open ? 'hold' : 'none')}
        reservation={reservation}
        loading={action.isPending}
        error={action.error}
        onSubmit={(optionExpiresAt) => action.mutate({ kind: 'hold', payload: optionExpiresAt })}
      />

      <CheckOutDialog
        open={dialog === 'checkOut'}
        onOpenChange={(open) => setDialog(open ? 'checkOut' : 'none')}
        reservation={reservation}
        loading={action.isPending}
        error={action.error}
        canCheckoutWithBalance={canCheckoutWithBalance}
        onRecordDeposit={actions.recordDeposit ? () => setDialog('deposit') : undefined}
        onSubmit={(values) => action.mutate({ kind: 'checkOut', payload: values })}
      />

      <CancelDialog
        open={dialog === 'cancel'}
        onOpenChange={(open) => setDialog(open ? 'cancel' : 'none')}
        reservation={reservation}
        loading={action.isPending}
        error={action.error}
        onSubmit={(values) => action.mutate({ kind: 'cancel', payload: values })}
      />

      <NoShowDialog
        open={dialog === 'noShow'}
        onOpenChange={(open) => setDialog(open ? 'noShow' : 'none')}
        reservation={reservation}
        loading={action.isPending}
        error={action.error}
        onSubmit={(values) => action.mutate({ kind: 'noShow', payload: values })}
      />

      <PaymentDrawer
        open={dialog === 'deposit'}
        onOpenChange={(open) => setDialog(open ? 'deposit' : 'none')}
        title={t('reservations:actions.recordDepositTitle')}
        context={{
          customerId: reservation.billingParty.id,
          customerName: reservation.billingParty.displayName,
          reservationId: reservation.id,
          reservationReference: reservation.reference,
          reservationBalance: reservation.totals.balance,
        }}
        onRecorded={() => void invalidate()}
      />

      {/* Confirmation : avance requise (D-020), sauf dérogation motivée. */}
      <Dialog
        open={dialog === 'confirm'}
        onOpenChange={(open) => setDialog(open ? 'confirm' : 'none')}
      >
        <DialogContent
          title={t('reservations:actions.confirmTitle', { reference: reservation.reference })}
          description={t('reservations:actions.confirmBody')}
          icon={<Check className="ico ico-16" aria-hidden="true" />}
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">{t('common:actions.cancel')}</Button>
              </DialogClose>
              <Button
                variant="primary"
                loading={action.isPending}
                disabled={
                  reservation.totals.depositReceived <= 0 &&
                  (!canConfirmWithoutDeposit || withoutDepositReason.trim().length < 10)
                }
                onClick={() =>
                  action.mutate({
                    kind: 'confirm',
                    payload:
                      reservation.totals.depositReceived <= 0
                        ? withoutDepositReason.trim()
                        : undefined,
                  })
                }
              >
                {t('reservations:actions.confirm')}
              </Button>
            </>
          }
        >
          <div className="stack">
            {action.isError ? <ReservationErrorNotice error={action.error} /> : null}
            {reservation.totals.depositReceived > 0 ? (
              <Notice tone="success">
                <p className="mb-0 t-13">
                  {t('reservations:actions.confirmDepositOk', {
                    amount: formatAmount(reservation.totals.depositReceived, i18n.language),
                  })}
                </p>
              </Notice>
            ) : canConfirmWithoutDeposit ? (
              <>
                <Notice tone="warning">
                  <p className="mb-0 t-13">
                    {t('reservations:actions.confirmNoDepositWarning', {
                      amount: formatAmount(reservation.totals.net, i18n.language),
                    })}
                  </p>
                </Notice>
                <label className="label" htmlFor="without-deposit-reason">
                  {t('reservations:create.withoutDepositReason')}
                  <span className="req" aria-hidden="true">
                    *
                  </span>
                </label>
                <textarea
                  id="without-deposit-reason"
                  className="textarea"
                  rows={3}
                  value={withoutDepositReason}
                  onChange={(event) => setWithoutDepositReason(event.target.value)}
                />
                <span className="hint">{t('reservations:create.withoutDepositReasonHint')}</span>
              </>
            ) : (
              <Notice tone="danger" role="alert">
                <p className="mb-0 t-13">{t('reservations:actions.confirmDepositRequired')}</p>
              </Notice>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Envoi de la confirmation : e-mail actif, WhatsApp et PDF à venir. */}
      <Dialog open={dialog === 'send'} onOpenChange={(open) => setDialog(open ? 'send' : 'none')}>
        <DialogContent
          title={t('reservations:actions.sendTitle')}
          description={reservation.guest.email ?? reservation.guest.displayName}
          icon={<Mail className="ico ico-16" aria-hidden="true" />}
          closeLabel={t('common:actions.close')}
          footer={
            <>
              <DialogClose asChild>
                <Button variant="ghost">{t('common:actions.cancel')}</Button>
              </DialogClose>
              <Button
                variant="primary"
                loading={action.isPending}
                onClick={() => action.mutate({ kind: 'send', payload: { channels: ['EMAIL'] } })}
              >
                {t('reservations:actions.sendEmail')}
              </Button>
            </>
          }
        >
          <div className="stack stack--sm">
            <Notice tone="info">
              <p className="mb-0 t-13">{t('reservations:actions.sendBody')}</p>
            </Notice>
            {/* Canal WhatsApp : le serveur répond 501 NOT_IMPLEMENTED tant que
                le module Notifications n'est pas livré. */}
            <Button variant="secondary" block disabled>
              {t('reservations:actions.sendWhatsappSoon')}
            </Button>
            <Button variant="secondary" block asChild>
              <a href={confirmationDocumentUrl(reservation.id)} target="_blank" rel="noreferrer">
                {t('reservations:actions.sendDocument')}
              </a>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DocumentPreviewDialog
        document={previewDocument}
        open={previewDocument !== null}
        onOpenChange={(open) => !open && setPreviewDocument(null)}
      />

      <DocumentDeleteDialog
        document={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        loading={deleteMutation.isPending}
        error={deleteMutation.error}
        onConfirm={(reason) =>
          deleteTarget && deleteMutation.mutate({ id: deleteTarget.id, reason })
        }
      />

      <EditReservationDrawer open={editOpen} onOpenChange={setEditOpen} reservation={reservation} />
    </Container>
  );
}
